import React, { useState, useRef } from "react";
import { Modal } from "../../../components/ui/Modal";
import { Pill } from "../../../components/ui/Pill";
import type { Brand } from "../carte.types";

interface BrandingModalProps {
  brand: Brand;
  onSave: (brand: Brand) => void;
  onClose: () => void;
}

export function BrandingModal({ brand, onSave, onClose }: BrandingModalProps) {
  const [logo, setLogo] = useState(brand.logo);
  const [bgColor, setBgColor] = useState(brand.bgColor);
  const [textColor, setTextColor] = useState(brand.textColor);
  const [cardColor, setCardColor] = useState(brand.cardColor || "#141210");
  const fileRef = useRef<HTMLInputElement>(null);

  const handleLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = (ev) => setLogo(ev.target?.result as string);
    r.readAsDataURL(f);
    e.target.value = "";
  };

  const save = () => {
    onSave({ logo, bgColor, textColor, cardColor, feeder: brand.feeder });
    onClose();
  };

  const reset = () => {
    setLogo(null);
    setBgColor("#0A0908");
    setTextColor("#F0EDE8");
    setCardColor("#141210");
  };

  return (
    <Modal onClose={onClose} title="Personnalisation · Carte publique">
      <div className="text-base text-secondary leading-[1.7] mb-4">Personnalisez l'apparence de votre carte des vins publique.</div>

      <div className="mb-5">
        <label className="label">Logo de l'établissement</label>
        <div className="flex items-center gap-3.5">
          <div className={`w-[72px] h-[72px] rounded-lg border border-dashed ${logo ? "border-accent-border" : "border-border"} ${logo ? "bg-transparent" : "bg-foreground/[0.02]"} flex items-center justify-center overflow-hidden shrink-0`}>
            {logo ? <img src={logo} alt="Logo" className="w-full h-full object-contain" />
              : <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="2" y="2" width="20" height="20" rx="4" className="stroke-muted/20" strokeWidth="1" strokeDasharray="3 2" /><path d="M12 8v8M8 12h8" className="stroke-muted/20" strokeWidth="1" strokeLinecap="round" /></svg>}
          </div>
          <div className="flex-1">
            <button onClick={() => fileRef.current?.click()} className="btn-accent px-4 py-2 text-[12px] mb-1.5 block">
              {logo ? "Changer le logo" : "Charger un logo"}
            </button>
            {logo && <button onClick={() => setLogo(null)} className="text-sm text-error bg-transparent border-none cursor-pointer font-sans">Supprimer</button>}
            <input ref={fileRef} type="file" accept="image/*" onChange={handleLogo} className="hidden" />
          </div>
        </div>
      </div>

      <div className="mb-5">
        <label className="label">Couleurs</label>
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Fond", val: bgColor, set: setBgColor },
            { label: "Texte", val: textColor, set: setTextColor },
            { label: "Encarts", val: cardColor, set: setCardColor },
          ].map((c) => (
            <div key={c.label}>
              <div className="text-[12px] text-secondary mb-1.5">{c.label}</div>
              <div className="flex items-center gap-2">
                <input type="color" value={c.val} onChange={(e) => c.set(e.target.value)} className="w-10 h-10 rounded-sm border border-border cursor-pointer p-0 bg-transparent shrink-0" />
                <input type="text" value={c.val} onChange={(e) => c.set(e.target.value)} className="min-w-0 flex-1 bg-foreground/[0.025] border border-border rounded py-[7px] px-2.5 text-foreground text-[12px] font-mono outline-none" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <div className="text-sm tracking-[1.5px] text-tertiary uppercase mb-2">Aperçu</div>
        <div className="rounded-lg border border-border p-4" style={{ background: bgColor }}>
          <div className="flex items-center gap-3.5 mb-3">
            {logo && <img src={logo} alt="" className="w-9 h-9 object-contain rounded-sm" />}
            <div>
              <div className="text-xs tracking-[3px] uppercase opacity-60" style={{ color: textColor }}>Carte des Vins</div>
              <div className="text-xl font-light" style={{ color: textColor }}>Mon Établissement</div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="rounded-md px-3 py-2.5 flex justify-between items-center" style={{ background: cardColor }}>
              <div>
                <div className="text-[13px] font-light" style={{ color: textColor }}>Gevrey-Chambertin</div>
                <div className="text-[11px] opacity-70" style={{ color: textColor }}>Rossignol-Trapet · 2019</div>
              </div>
              <div className="text-[13px] text-gold">68 €</div>
            </div>
            <div className="rounded-md px-3 py-2.5 flex justify-between items-center" style={{ background: cardColor }}>
              <div>
                <div className="text-[13px] font-light" style={{ color: textColor }}>Meursault Les Narvaux</div>
                <div className="text-[11px] opacity-70" style={{ color: textColor }}>Domaine Roulot · 2020</div>
              </div>
              <div className="text-[13px] text-gold">82 €</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <button onClick={reset} className="btn-secondary px-3.5 py-2 text-[12px] shrink-0">Réinitialiser</button>
        <Pill active onClick={save} style={{ flex: 1 }}>Appliquer →</Pill>
      </div>
    </Modal>
  );
}
